import { Box, Button, InputBase, Typography } from "@mui/material";
import CustomAxios from "../../utils/CustomAxios";
import { useEffect, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import * as Yup from 'yup';
import { Toast } from "primereact/toast";

export function AppVersionControlSettings() {
    const [formData, setFormData] = useState({
        platform: "android",
        latest_version: "",
        min_supported_version: "",
        update_message: "",
        store_url: "",
    });
    const toast = useRef(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const user_role = localStorage.getItem('role');

    const handleChange = (e) => {
        const { name, value } = e.target ? e.target : e; // handle dropdown case
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const submitForm = async () => {
        try {
            setErrors({});

            await versionSchema.validate(formData, { abortEarly: false });

            setLoading(true);

            let response;

            if (formData.id) {
                // Update existing app version
                response = await CustomAxios.put(`app-version/${formData.id}/`, formData);
            } else {
                // Create new app version
                response = await CustomAxios.post(`app-version/`, formData);
            }

            toast.current.show({ 
                severity: 'success', 
                summary: 'Success', 
                detail: formData.id ? `App Version Updated Successfully` : `App Version Created Successfully`, 
                life: 3000 
            });
            setLoading(false);

        } catch (validationError) {
            if (validationError.name === 'ValidationError') {
                const yupErrors = {};
                validationError.inner.forEach(err => {
                    yupErrors[err.path] = err.message;
                });
                setErrors(yupErrors); // Show Yup validation errors
            } else {
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Error', 
                    detail: formData.id ? `App Version Failed Update` : `App Version Failed Create`, 
                    life: 3000 
                });
                console.error("Submit error:", validationError);
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchAppVersion = async () => {
            if (!formData.platform) return;

            try {
                const response = await CustomAxios.get(`app-version/`, {
                    params: { platform: formData.platform },
                });

                const data = response.data;

                if (data.length > 0) {
                    const version = data[0]; // Assuming one record per platform
                    setFormData(prev => ({
                        ...prev,
                        id: version.id,
                        latest_version: version.latest_version,
                        min_supported_version: version.min_supported_version,
                        update_message: version.update_message,
                        store_url: version.store_url,
                    }));
                } else {
                    setFormData(prev => ({
                        ...prev,
                        id: null,
                        latest_version: '',
                        min_supported_version: '',
                        update_message: '',
                        store_url: '',
                    }));
                }
            } catch (error) {
                console.error('API error while fetching version:', error);
            }
        };

        fetchAppVersion();
    }, [formData.platform]);

    const versionSchema = Yup.object().shape({
        platform: Yup.string().required("Platform is required."),
        latest_version: Yup.string()
            .matches(/^\d+\.\d+\.\d+$/, "Must be in x.y.z format")
            .required("Latest version is required."),
        min_supported_version: Yup.string()
            .matches(/^\d+\.\d+\.\d+$/, "Must be in x.y.z format")
            .required("Minimum version is required."),
        update_message: Yup
            .string()
            .required('Update message is required')
            .min(10, 'Update message should be at least 10 characters'),
        store_url: Yup.string()
            .url("Must be a valid URL")
            .required("Update URL is required."),
    });

    // Check if user can control app version settings (after all hooks)
    const canControlAppVersion = ['super_admin', 'admin'].includes(user_role);

    if (!canControlAppVersion) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", padding: "20px" }}>
                <Typography variant="h6" color="error">
                    Access Denied
                </Typography>
                <Typography>
                    You don't have permission to control app version settings.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%", padding: "30px" }}>
            <Toast ref={toast} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Typography sx={{ fontSize: "16px", fontWeight: "700", lineHeight: "24px" }}>Settings</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "22px" }}>App Version Control settings</Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", width: "100%", background: "#FFF", height: "100%", padding: "20px", gap: "20px" }}>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>Platform<span style={{ color: "red", marginLeft: "5px" }}>*</span></Typography>
                    <Dropdown
                        value={formData.platform}
                        onChange={handleChange}
                        options={[{ label: "Android", value: "android" }, { label: "iOS", value: "ios" }]}
                        name="platform"
                        placeholder="Select Platform"
                        className="w-full md:w-14rem"
                    />
                    {errors.platform && (
                        <Typography sx={{ color: "red", fontSize: "12px" }}>{errors.platform}</Typography>
                    )}
                </Box>

                {/* Latest Version */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>

                    <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>Latest Version<span style={{ color: "red", marginLeft: "5px" }}>*</span></Typography>
                    <InputBase
                        value={formData.latest_version}
                        onChange={handleChange}
                        name="latest_version"
                        placeholder="e.g. 1.2.3"
                        sx={{
                            backgroundColor: "white",
                            width: "100%",
                            height: "32px",
                            borderRadius: "2px",
                            border: "1px solid",
                            borderColor: "secondary.border",
                            padding: "5px 12px",
                            fontSize: "14px"
                        }}
                    />
                    {errors.latest_version && (
                        <Typography sx={{ color: "red", fontSize: "12px" }}>{errors.latest_version}</Typography>
                    )}
                </Box>

                {/* Minimum Supported Version */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>

                    <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>Minimum Supported Version<span style={{ color: "red", marginLeft: "5px" }}>*</span></Typography>
                    <InputBase
                        value={formData.min_supported_version}
                        onChange={handleChange}
                        name="min_supported_version"
                        placeholder="e.g. 1.1.0"
                        sx={{
                            backgroundColor: "white",
                            width: "100%",
                            height: "32px",
                            borderRadius: "2px",
                            border: "1px solid",
                            borderColor: "secondary.border",
                            padding: "5px 12px",
                            fontSize: "14px"
                        }}
                    />
                    {errors.min_supported_version && (
                        <Typography sx={{ color: "red", fontSize: "12px" }}>{errors.min_supported_version}</Typography>
                    )}
                </Box>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Update Message<span style={{ color: "red", marginLeft: "5px" }}>*</span></Typography>
                    <InputBase
                        value={formData.update_message}
                        onChange={handleChange}
                        name="update_message"
                        placeholder="What's new in this version?"
                        multiline
                        rows={3}
                        sx={{ backgroundColor: "white", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "8px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                    {errors.update_message && (
                        <Typography style={{ color: "red", fontSize: "12PX" }}>{errors.update_message}</Typography>
                    )}
                </Box>
                {/* Update URL */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>

                    <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>Store URL<span style={{ color: "red", marginLeft: "5px" }}>*</span></Typography>
                    <InputBase
                        value={formData.store_url}
                        onChange={handleChange}
                        name="store_url"
                        placeholder="e.g. https://play.google.com/store/apps/details?id=..."
                        sx={{
                            backgroundColor: "white",
                            width: "100%",
                            height: "32px",
                            borderRadius: "2px",
                            border: "1px solid",
                            borderColor: "secondary.border",
                            padding: "5px 12px",
                            fontSize: "14px"
                        }}
                    />
                    {errors.store_url && (
                        <Typography sx={{ color: "red", fontSize: "12px" }}>{errors.store_url}</Typography>
                    )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%", marginTop: '20px' }}>

                    <Button variant="contained" disabled={loading ? true : false} onClick={() => submitForm()} sx={{ textTransform: "none", opacity: loading && .5, width: '30%' }}><Typography sx={{ fontSize: { sm: "12px", md: "14px" }, fontWeight: "400", lineHeight: "22px", textAlign: "center", color: loading && 'primary.button' }}>{loading ? ' Saving App Version...' : ' Save App Version'}</Typography> </Button>

                </Box>

            </Box>


        </Box>
    )
}